import React, { useState, useMemo, useRef, useCallback, startTransition, memo } from 'react';
import PropTypes from 'prop-types';
import OmButton from '../../ui/OmButton';
import OmDocumentUpload from '../../ui/OmDocumentUpload';
import OmSelect from '../../ui/OmSelect';
import OmSelectOption from '../../ui/OmSelectOption';
import { getClaimTypeDisplayName } from '../../../services/progressiveDisclosureService';

/**
 * Isolated Native Form wrapper to prevent React DOM reconciliation issues with Shadow DOM
 * This component is memoized and only re-renders when essential props change
 */
const IsolatedDocumentUploadForm = memo(({ 
  children, 
  onSubmit, 
  onPrevious, 
  isLoading,
  titleText,
  descriptionText 
}) => {
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Form Header - matching OM Design System styling */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{titleText}</h2>
        {descriptionText && (
          <p className="text-sm text-gray-600">{descriptionText}</p>
        )}
      </div>
      
      {/* Form Content */}
      <form 
        id="document-upload-form"
        onSubmit={onSubmit}
        className="px-6 py-4"
      >
        {children}
        
        {/* Form Actions */}
        <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-200">
          <div>
            <OmButton
              type="button"
              onClick={onPrevious}
              disabled={isLoading}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="arrow_back"
              style={{
                '--omk-outlined-button-label-text-color': 'var(--color-steel)',
                '--omk-outlined-button-icon-color': 'var(--color-steel)',
                '--omk-outlined-button-outline-color': 'var(--color-silver-mist)',
                '--omk-outlined-button-hover-label-text-color': 'var(--color-deep-jungle)',
                '--omk-outlined-button-hover-icon-color': 'var(--color-deep-jungle)',
                '--omk-outlined-button-hover-state-layer-color': 'var(--color-silver-mist)'
              }}
            >
              Previous
            </OmButton>
          </div>
          
          <OmButton
            type="submit"
            disabled={isLoading}
            buttonType="filled-button"
            buttonSize="medium"
            trailingIcon="arrow_forward"
          >
            Continue to Summary
          </OmButton>
        </div>
      </form>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if essential props change
  return (
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.titleText === nextProps.titleText &&
    prevProps.descriptionText === nextProps.descriptionText &&
    prevProps.onSubmit === nextProps.onSubmit &&
    prevProps.onPrevious === nextProps.onPrevious
  );
});

IsolatedDocumentUploadForm.displayName = 'IsolatedDocumentUploadForm';

/**
 * Step 4: Document Upload Component
 * Handles file upload with document type selection, file management, and requirements tracking
 */
const Step4DocumentUpload = ({
  formData,
  errors,
  validationErrors,
  uploadedFiles,
  officers,
  isLoading,
  onInputChange,
  onFieldBlur,
  onFileUpload,
  onFileRemove,
  onNext,
  onPrevious,
  currentStep,
  schema,
  showError
}) => {
  // Combined state for document type selection to reduce re-renders
  const [documentTypeState, setDocumentTypeState] = useState({
    selectedDocumentType: '',
    customDocumentType: '',
    showCustomInput: false
  });
  
  // Refs to prevent React reconciliation issues
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  
  // Debounced state update function to batch multiple state changes
  const updateDocumentTypeState = useCallback((updates) => {
    startTransition(() => {
      setDocumentTypeState(prev => ({
        ...prev,
        ...updates
      }));
    });
  }, []);
  
  // Clear all document type state in one batched update
  const clearDocumentTypeState = useCallback(() => {
    updateDocumentTypeState({
      selectedDocumentType: '',
      customDocumentType: '',
      showCustomInput: false
    });
  }, [updateDocumentTypeState]);
  
  // Extract values for easier use
  const { selectedDocumentType, customDocumentType, showCustomInput } = documentTypeState;

  // Get required documents for the current claim type
  const getRequiredDocuments = useCallback(() => {
    if (!schema?.requiredDocuments || !formData.type) return [];
    return schema.requiredDocuments[formData.type] || [];
  }, [schema?.requiredDocuments, formData.type]);

  // Get document type options for dropdown
  const getDocumentTypeOptions = useCallback(() => {
    if (!schema?.documentTypes) return [];
    
    const required = getRequiredDocuments();
    const requiredOptions = schema.documentTypes.filter(doc => 
      required.includes(doc.value)
    );
    const otherOption = schema.documentTypes.find(doc => doc.value === 'OTHER');
    
    return [...requiredOptions, ...(otherOption ? [otherOption] : [])];
  }, [schema?.documentTypes, getRequiredDocuments]);

  // Calculate upload progress for required documents
  const getUploadProgress = useMemo(() => {
    const required = getRequiredDocuments();
    const uploadedDocTypes = uploadedFiles
      .filter(f => f.documentType && f.documentType !== 'OTHER')
      .map(f => f.documentType);
    
    const completed = required.filter(doc => uploadedDocTypes.includes(doc));
    
    return {
      total: required.length,
      completed: completed.length,
      missing: required.filter(doc => !uploadedDocTypes.includes(doc)),
      percentage: required.length > 0 ? Math.round((completed.length / required.length) * 100) : 100
    };
  }, [getRequiredDocuments, uploadedFiles]);

  // Get document type label from value
  const getDocumentTypeLabel = useCallback((value) => {
    if (!schema?.documentTypes) return value;
    const docType = schema.documentTypes.find(doc => doc.value === value);
    return docType ? docType.label : value;
  }, [schema?.documentTypes]);
  
  // Generate structured filename
  const generateFileName = useCallback((originalName, documentType, isOther = false, userSpecifiedType = '') => {
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').slice(0, 15);
    const sanitizedClaimant = formData.claimantName ? 
      formData.claimantName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '') : 
      'Claimant';
    const extension = originalName.split('.').pop();
    
    if (isOther && userSpecifiedType) {
      const sanitizedType = userSpecifiedType.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      return `${sanitizedClaimant}_OTHER_${sanitizedType}_${timestamp}.${extension}`;
    }
    
    // Handle versioning for required documents
    const existingVersions = uploadedFiles.filter(f => 
      f.documentType === documentType
    );
    const version = existingVersions.length + 1;
    
    return `${sanitizedClaimant}_${documentType}_v${version}_${timestamp}.${extension}`;
  }, [formData.claimantName, uploadedFiles]);

  // Handle document type selection
  const handleDocumentTypeChange = useCallback((e) => {
    const value = e.target.value;
    const isOther = value === 'OTHER';
    
    updateDocumentTypeState({
      selectedDocumentType: value,
      showCustomInput: isOther,
      ...(isOther ? {} : { customDocumentType: '' })
    });
  }, [updateDocumentTypeState]);

  // Handle form submission for this step
  const handleStepSubmit = (e) => {
    e.preventDefault();
    if (onNext) {
      onNext();
    }
  };

  // Upload function that returns a Promise (required by OmDocumentUpload)
  const uploadFunction = useCallback(async (file) => {

    // Return a promise that resolves immediately since we handle the upload in the parent
    return new Promise((resolve, reject) => {
      try {
        const { selectedDocumentType, customDocumentType } = documentTypeState;
        
        // Perform the same validation and processing as before
        if (!selectedDocumentType) {
          reject(new Error('Please select a document type before uploading files.'));
          return;
        }

        if (selectedDocumentType === 'OTHER' && !customDocumentType.trim()) {
          reject(new Error('Please specify the document type for "Other" documents.'));
          return;
        }

        // Validate file type and size
        const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
        const maxSize = 10 * 1024 * 1024; // 10MB
        
        if (!validTypes.includes(file.type)) {
          reject(new Error(`File ${file.name} has an invalid type. Please upload PDF, JPG, PNG, DOC, or DOCX files.`));
          return;
        }
        
        if (file.size > maxSize) {
          reject(new Error(`File ${file.name} is too large. Maximum size is 10MB.`));
          return;
        }

        // Check total file limit
        if (uploadedFiles.length >= 10) {
          reject(new Error('Maximum 10 files allowed. Please remove some files before adding new ones.'));
          return;
        }

        // Enhance file with metadata and structured naming
        const isOther = selectedDocumentType === 'OTHER';
        const newFileName = generateFileName(
          file.name, 
          selectedDocumentType, 
          isOther, 
          customDocumentType
        );

        // Create new file with structured name
        const renamedFile = new File([file], newFileName, { type: file.type });
        
        // Add metadata
        renamedFile.documentType = selectedDocumentType;
        renamedFile.isRequired = !isOther && getRequiredDocuments().includes(selectedDocumentType);
        renamedFile.originalName = file.name;
        renamedFile.customType = isOther ? customDocumentType : null;

        // Don't clear document type state - allow multiple uploads of same type
        // Users can manually change document type if they want to upload different types

        // Resolve the promise to indicate successful processing
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }, [documentTypeState, uploadedFiles, getRequiredDocuments, generateFileName, onFileUpload, clearDocumentTypeState]);

  // Handle file upload with validation and document type
  const handleFileUpload = useCallback((file, event) => {

    const { selectedDocumentType, customDocumentType } = documentTypeState;

    if (!selectedDocumentType) {
      showError('Please select a document type before uploading files.');
      return;
    }

    if (selectedDocumentType === 'OTHER' && !customDocumentType.trim()) {
      showError('Please specify the document type for "Other" documents.');
      return;
    }

    // Validate file type and size
    const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    if (!validTypes.includes(file.type)) {
      showError(`File ${file.name} has an invalid type. Please upload PDF, JPG, PNG, DOC, or DOCX files.`);
      return;
    }
    
    if (file.size > maxSize) {
      showError(`File ${file.name} is too large. Maximum size is 10MB.`);
      return;
    }

    // Check total file limit
    if (uploadedFiles.length >= 10) {
      showError('Maximum 10 files allowed. Please remove some files before adding new ones.');
      return;
    }

    // Enhance file with metadata and structured naming
    const isOther = selectedDocumentType === 'OTHER';
    const newFileName = generateFileName(
      file.name, 
      selectedDocumentType, 
      isOther, 
      customDocumentType
    );

    // Create new file with structured name
    const renamedFile = new File([file], newFileName, { type: file.type });
    
    // Add metadata
    renamedFile.documentType = selectedDocumentType;
    renamedFile.isRequired = !isOther && getRequiredDocuments().includes(selectedDocumentType);
    renamedFile.originalName = file.name;
    renamedFile.customType = isOther ? customDocumentType : null;

    // Call parent handler with single file
    onFileUpload([renamedFile]);
    
    // Don't clear selections after upload - allow multiple uploads of same or different types
    // Users can manually change document type if needed
  }, [documentTypeState, uploadedFiles, getRequiredDocuments, generateFileName, onFileUpload, showError]);

  // Handle file upload errors
  const handleFileError = useCallback((error, event) => {
    
    if (error && error.message) {
      showError(error.message);
    } else if (typeof error === 'string') {
      showError(error);
    } else {
      showError('An error occurred while uploading the file. Please try again.');
    }
  }, [showError]);

  // Cancel upload callback (required by OmDocumentUpload)
  const cancelUpload = useCallback(() => {
    // Handle upload cancellation if needed
  }, []);

  // Delete file callback (required by OmDocumentUpload)
  const deleteFile = useCallback(() => {
    // Handle file deletion if needed
  }, []);

  // Remove file on error callback (required by OmDocumentUpload)
  const removeFileOnError = useCallback(() => {
    // Handle file removal on error if needed
  }, []);

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <IsolatedDocumentUploadForm
        titleText="Document Upload"
        descriptionText="Upload supporting documents for your claim. You can review all information in the next step before final submission."
        onSubmit={handleStepSubmit}
        onPrevious={onPrevious}
        isLoading={isLoading}
      >
        {/* Required Documents Checklist */}
        {getRequiredDocuments().length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-3">
              Required Documents for {formData.type ? getClaimTypeDisplayName(formData.type) : 'This Claim'} 
              ({getUploadProgress.completed}/{getUploadProgress.total} uploaded)
            </h3>
            <div className="space-y-2">
              {getRequiredDocuments().map((docType, index) => {
                const isUploaded = uploadedFiles.some(f => f.documentType === docType);
                const uploadedCount = uploadedFiles.filter(f => f.documentType === docType).length;
                
                return (
                  <div key={index} className="flex items-center space-x-2">
                    {isUploaded ? (
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={`text-sm ${isUploaded ? 'text-green-700' : 'text-gray-600'}`}>
                      {getDocumentTypeLabel(docType)}
                      {isUploaded && uploadedCount > 1 && (
                        <span className="ml-1 text-green-600 font-medium">({uploadedCount} files)</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            
            {getUploadProgress.total > 0 && (
              <div className="mt-3">
                <div className="flex justify-between text-sm text-blue-700 mb-1">
                  <span>Progress</span>
                  <span>{getUploadProgress.percentage}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${getUploadProgress.percentage}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Document Type Selection and Upload */}
        <div className="space-y-4">
          <div className="space-y-4">
            <h3 className="font-semibold text-deep-jungle">Upload Documents</h3>
            
            {/* Document Type Selection */}
            <div className="space-y-2">
              <OmSelect
                label="Document Type"
                name="documentType"
                value={selectedDocumentType}
                onChange={handleDocumentTypeChange}
                required={true}
                disabled={isLoading}
                helperText="Select the type of document you want to upload"
              >
                <OmSelectOption value="">Select document type...</OmSelectOption>
                {getDocumentTypeOptions().map((docType, index) => (
                  <OmSelectOption key={index} value={docType.value}>
                    {docType.label}
                    {getRequiredDocuments().includes(docType.value) && ' (Required)'}
                  </OmSelectOption>
                ))}
              </OmSelect>
            </div>

            {/* Custom Document Type Input for 'Other' */}
            {showCustomInput && (
              <div className="space-y-2">
                <label className="form-label">
                  Specify Document Type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customDocumentType}
                  onChange={(e) => updateDocumentTypeState({ customDocumentType: e.target.value })}
                  placeholder="Enter document type (e.g., Medical Invoice, Police Report)"
                  className="form-input"
                  disabled={isLoading}
                />
                <p className="text-sm text-gray-600">
                  Please specify what type of document you're uploading
                </p>
              </div>
            )}

            {/* File Upload */}
            <div className="space-y-2">
              <label className="form-label">
                Select Files
              </label>
              <p className="form-help">
                Upload files (PDF, JPG, PNG, DOC, DOCX). Maximum 10 files total, 10MB each.
                You can upload multiple versions of the same document type.
              </p>
              <OmDocumentUpload
                ref={fileInputRef}
                placeholder={selectedDocumentType ? "Click to upload document" : "Select document type first"}
                secondaryPlaceholder="PDF, JPG, PNG, DOC, DOCX. Max 10MB"
                acceptedFileTypes="application/pdf,image/jpeg,image/jpg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                maxFileSizeInBytes={10485760} // 10MB
                disabled={isLoading || !selectedDocumentType}
                uploadFunction={uploadFunction}
                cancelUpload={cancelUpload}
                deleteFile={deleteFile}
                removeFileOnError={removeFileOnError}
                onFileSelect={handleFileUpload}
                onError={handleFileError}
                customErrorMessage={!selectedDocumentType ? "Please select a document type first" : ""}
                progress={0}
              />
              {!selectedDocumentType && (
                <p className="text-sm text-orange-600">
                  Please select a document type above before uploading files
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <div key={`file-list-${uploadedFiles.length}-${uploadedFiles.map(f => f.name + f.size).join('-')}`} className="space-y-4">
            <h4 className="font-medium text-deep-jungle">Uploaded Files ({uploadedFiles.length}/10):</h4>
            
            {/* Required Documents */}
            {uploadedFiles.some(f => f.isRequired) && (
              <div className="space-y-2">
                <h5 className="font-medium text-green-700">Required Documents:</h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(() => {
                    return uploadedFiles
                      .filter(f => f.isRequired)
                      .map((file) => (
                        <div key={`required-${file.name}-${file.size}-${file.lastModified}`} className="file-item bg-green-50 border-green-200">
                          <div className="file-item-info">
                            <p className="file-item-name">{file.name}</p>
                            <p className="file-item-size">{formatFileSize(file.size)}</p>
                            <p className="text-xs text-green-600 font-medium">
                              {getDocumentTypeLabel(file.documentType)}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => onFileRemove(uploadedFiles.indexOf(file))}
                            className="file-remove-btn"
                            disabled={isLoading}
                          >
                            Remove
                          </button>
                        </div>
                      ));
                  })()}
                </div>
              </div>
            )}

            {/* Other Documents */}
            {uploadedFiles.some(f => !f.isRequired) && (
              <div className="space-y-2">
                <h5 className="font-medium text-blue-700">Other Documents:</h5>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(() => {
                    return uploadedFiles
                      .filter(f => !f.isRequired)
                      .map((file) => (
                      <div key={`other-${file.name}-${file.size}-${file.lastModified}`} className="file-item bg-blue-50 border-blue-200">
                        <div className="file-item-info">
                          <p className="file-item-name">{file.name}</p>
                          <p className="file-item-size">{formatFileSize(file.size)}</p>
                          <p className="text-xs text-blue-600 font-medium">
                            {file.documentType === 'OTHER' ? file.customType || 'Other Document' : getDocumentTypeLabel(file.documentType)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => onFileRemove(uploadedFiles.indexOf(file))}
                          className="file-remove-btn"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      </div>
                      ));
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Document Upload Errors */}
        {errors.documents && (
          <p className="form-error">{errors.documents}</p>
        )}

        {/* Submission Options */}
        <div className="space-y-4">
          
        </div>
      </IsolatedDocumentUploadForm>
    </div>
  );
};

Step4DocumentUpload.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  validationErrors: PropTypes.object,
  uploadedFiles: PropTypes.array.isRequired,
  officers: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onFieldBlur: PropTypes.func,
  onFileUpload: PropTypes.func.isRequired,
  onFileRemove: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  currentStep: PropTypes.number.isRequired,
  schema: PropTypes.object,
  showError: PropTypes.func.isRequired
};

Step4DocumentUpload.defaultProps = {
  validationErrors: {},
  onFieldBlur: null,
  onPrevious: null,
  schema: null
};

export default Step4DocumentUpload;